import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Table, Form, InputGroup, Modal, Alert, Spinner } from 'react-bootstrap';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Archive, 
  Settings, 
  Users, 
  MapPin,
  Phone,
  Calendar,
  TrendingUp,
  Eye,
  RefreshCw,
  RotateCcw,
  Pause,
  Trash2,
  Edit3
} from 'lucide-react';
import { boutiqueService } from '../../services/boutiqueService';
import CreateBoutique from './CreateBoutique';
import BoutiqueCard from './BoutiqueCard';
import BoutiqueConfigModal from './BoutiqueConfigModal';  // ✅ AJOUT
import DeleteSalesConfirmationModal from './DeleteSalesConfirmationModal';
import { boutiqueAdminService } from '../../services/boutiqueService';

const BoutiquesList = () => {
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [errors, setErrors] = useState({});

  const [showDeleteBoutiqueModal, setShowDeleteBoutiqueModal] = useState(false);
  const [selectedBoutiqueForDelete, setSelectedBoutiqueForDelete] = useState(null);
  const [boutiqueDeleteConfirmData, setBoutiqueDeleteConfirmData] = useState(null);
  
  const [showDeleteSalesModal, setShowDeleteSalesModal] = useState(false);
  const [selectedBoutiqueForConfig, setSelectedBoutiqueForConfig] = useState(null);
  const [selectedBoutiqueForDeleteSales, setSelectedBoutiqueForDeleteSales] = useState(null);
  const [salesConfirmationData, setSalesConfirmationData] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);


  // Filtres et recherche
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    sortBy: 'created_date',
    sortOrder: 'desc'
  });

  // État pour les statistiques globales
  const [globalStats, setGlobalStats] = useState({
    totalBoutiques: 0,
    activeBoutiques: 0,
    totalRevenue: 0,
    totalManagers: 0
  });

  useEffect(() => {
    loadBoutiques();
  }, []);

  // ===================================================================
  // FONCTIONS UTILITAIRES
  // ===================================================================

  // 🔔 Afficher notification
  const showNotification = (type, title, message) => {
    // TODO: Remplacer par un vrai système de notification (toast, etc.)
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { variant: 'success', text: 'Active' },
      INACTIVE: { variant: 'warning', text: 'Inactive' },
      ARCHIVED: { variant: 'secondary', text: 'Archivée' },
      SUSPENDED: { variant: 'danger', text: 'Suspendue' }
    };
    
    const config = statusConfig[status] || statusConfig.ACTIVE;
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };
  

  const clearErrors = () => {
    setErrors({});
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // ===================================================================
  // FONCTIONS DE CHARGEMENT ET ACTIONS
  // ===================================================================

  // 📊 Charger les boutiques
  const loadBoutiques = async () => {
    try {
      setLoading(true);
      const response = await boutiqueService.getAllBoutiques();
      
      if (response.success) {
        const boutiquesData = response.data || [];
        setBoutiques(boutiquesData);
        
        
        // Calculer les statistiques
        const calculatedStats = {
          totalBoutiques: boutiquesData.length,
          activeBoutiques: boutiquesData.filter(b => 
            b.status === 'ACTIVE' || b.status === 'ACTIF' || b.isActive === true
          ).length,
          totalRevenue: boutiquesData.reduce((sum, b) => {
            return sum + (b.totalRevenue || b.revenue || 0);
          }, 0),
          totalManagers: boutiquesData.filter(b => 
            (b.manager_id || b.manager_name) && b.manager_is_active !== false
          ).length
        };
        
        setGlobalStats(calculatedStats);
        
      } else {
        console.error('Erreur lors du chargement des boutiques:', response.message);
        setBoutiques([]);
        showNotification('error', 'Erreur', response.message);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setBoutiques([]);
      showNotification('error', 'Erreur', 'Impossible de charger les boutiques');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBoutique = async (boutique) => {
    setSelectedBoutiqueForDelete(boutique);
    setDeleteLoading(true);
    try {
        const response = await boutiqueAdminService.getBoutiqueDeleteConfirmation(boutique.id);
        if (response.success) {
            setBoutiqueDeleteConfirmData(response.data);
            setShowDeleteBoutiqueModal(true);
        } else {
            showNotification('error', 'Erreur', response.message);
        }
    } catch (error) {
        showNotification('error', 'Erreur', 'Impossible de récupérer les données');
    } finally {
        setDeleteLoading(false);
    }
};

const handleConfirmDeleteBoutique = async () => {
    if (!selectedBoutiqueForDelete) return;
    setDeleteLoading(true);
    try {
        const response = await boutiqueAdminService.deleteBoutiqueCompletely(selectedBoutiqueForDelete.id);
        if (response.success) {
            const d = response.data;
            showNotification('success', '✅ Boutique supprimée', 
                `${d.boutiqueName} supprimée avec : ${d.deletedProductsCount} produits, ${d.deletedSalesCount} ventes, ${d.deletedCustomersCount} clients, ${d.deletedUsersCount} utilisateurs`
            );
            setShowDeleteBoutiqueModal(false);
            setSelectedBoutiqueForDelete(null);
            loadBoutiques();
        } else {
            showNotification('error', 'Erreur', response.message);
        }
    } catch (error) {
        showNotification('error', 'Erreur', 'Erreur lors de la suppression');
    } finally {
        setDeleteLoading(false);
    }
};

  // ✅ Créer une boutique
  const handleCreateBoutique = () => {
    setSelectedBoutique(null);
    clearErrors();
    setShowCreateModal(true);
  };

  // ✏️ Modifier une boutique
  const handleEditBoutique = (boutique) => {
    setSelectedBoutique(boutique);
    clearErrors();
    setShowCreateModal(true);
  };

  // 👁️ Voir les détails
  const handleViewDetails = (boutique) => {
    setSelectedBoutique(boutique);
    setShowDetailsModal(true);
  };

  // ✅ NOUVELLE FONCTION : Gérer la suppression des ventes
  const handleDeleteSales = async (boutique) => {
    setSelectedBoutiqueForDeleteSales(boutique);
    setDeleteLoading(true);

    try {
      // Récupérer les données de confirmation
      const response = await boutiqueAdminService.getSalesDeleteConfirmation(boutique.id);
      
      if (response.success) {
        setSalesConfirmationData(response.data);
        setShowDeleteSalesModal(true);
      } else {
        showNotification('error', 'Erreur', response.message);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      showNotification('error', 'Erreur', 'Impossible de récupérer les données de vente');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ✅ NOUVELLE FONCTION : Confirmer la suppression des ventes
  const handleConfirmDeleteSales = async (deleteStocks = false) => {
  if (!selectedBoutiqueForDeleteSales) return;

  setDeleteLoading(true);
  try {
    
    const response = await boutiqueAdminService.deleteAllBoutiqueSales(selectedBoutiqueForDeleteSales.id, deleteStocks);
    
    if (response.success) {
      // ✅ NOUVEAU : Message de succès détaillé avec infos stocks
      const data = response.data;
      const successMessage = `Suppression complète de ${selectedBoutiqueForDeleteSales.name} réussie !
      

📊 Résumé de la suppression :
• ${data.deletedSalesCount || 0} ventes supprimées (${formatCurrency(data.deletedRevenue || 0)})
• ${data.resetProductsCount || 0} produits remis à stock zéro (${data.resetStockQuantity || 0} unités)
• ${data.deletedMovementsCount || 0} mouvements de stock supprimés


⚠️ Toutes les données ont été définitivement supprimées.`;
      
      showNotification('success', '✅ Suppression complète réussie', successMessage);
      
      // Fermer le modal et rafraîchir
      setShowDeleteSalesModal(false);
      setSelectedBoutiqueForDeleteSales(null);
      setSalesConfirmationData(null);
      
      // Rafraîchir la liste des boutiques
      loadBoutiques();
    } else {
      showNotification('error', 'Erreur de suppression', response.message);
    }
  } catch (error) {
    console.error('Erreur lors de la suppression complète:', error);
    showNotification('error', 'Erreur', 'Erreur lors de la suppression complète des données');
  } finally {
    setDeleteLoading(false);
  }
};


  // ✅ Fonction pour fermer le modal de suppression
  const handleCloseDeleteSalesModal = () => {
    setShowDeleteSalesModal(false);
    setSelectedBoutiqueForDeleteSales(null);
    setSalesConfirmationData(null);
  };

  // ⚙️ Configurer une boutique  ✅ AJOUT
  const handleConfigureBoutique = (boutique) => {
    setSelectedBoutiqueForConfig(boutique);
    setShowConfigModal(true);
  };

  const handleCloseConfig = () => {
    setShowConfigModal(false);
    setSelectedBoutiqueForConfig(null);
  };

  // 💾 Sauvegarder la configuration  ✅ AJOUT
  const handleSaveConfig = (updatedBoutique) => {
    
    // Mettre à jour la liste des boutiques
    setBoutiques(prev => prev.map(b => 
      b.id === updatedBoutique.id ? { ...b, ...updatedBoutique } : b
    ));
    
    // Rafraîchir la liste depuis le serveur
    loadBoutiques();
    
    // Message de succès
    showNotification('success', 'Configuration mise à jour', 
      `La configuration de ${updatedBoutique.name} a été mise à jour avec succès`);
  };

  // 🗄️ Archiver une boutique
  const handleArchiveBoutique = async (boutiqueId) => {
    try {
      const boutique = boutiques.find(b => b.id === boutiqueId);
      if (!boutique) {
        showNotification('error', 'Erreur', 'Boutique introuvable');
        return;
      }

      const confirmMessage = `⚠️ ATTENTION: Archiver la boutique "${boutique.name}" ?

Cette action aura les conséquences suivantes :
• La boutique sera marquée comme ARCHIVÉE
• Les employés ne pourront plus se connecter
• Les ventes seront suspendues
• Les données restent conservées

Cette action peut être annulée plus tard.

Voulez-vous continuer ?`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      setLoading(true);

      const response = await boutiqueService.archiveBoutique(boutiqueId);
      
      if (response.success) {
        showNotification('success', 'Boutique archivée', `${boutique.name} a été archivée avec succès`);
        await loadBoutiques();
      } else {
        showNotification('error', 'Erreur d\'archivage', response.message);
      }

    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
      showNotification('error', 'Erreur', 'Impossible d\'archiver la boutique');
    } finally {
      setLoading(false);
    }
  };

  // 💾 Sauvegarder une boutique (création/modification)
  const handleSaveBoutique = async (boutiqueData) => {
    try {
      setLoading(true);
      clearErrors();

      const response = selectedBoutique 
        ? await boutiqueService.updateBoutique(selectedBoutique.id, boutiqueData)
        : await boutiqueService.createBoutique(boutiqueData);

      if (response.success) {
        const action = selectedBoutique ? 'modifiée' : 'créée';
        showNotification('success', 'Sauvegarde réussie', `Boutique ${action} avec succès`);
        
        setShowCreateModal(false);
        setSelectedBoutique(null);
        await loadBoutiques();
        
        return response;
      } else {
        setErrors({ submit: response.message });
        showNotification('error', 'Erreur de sauvegarde', response.message);
        return response;
      }

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = 'Erreur lors de la sauvegarde';
      setErrors({ submit: errorMessage });
      showNotification('error', 'Erreur', 'Impossible de sauvegarder');
      
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Composant de statistique
  const StatCard = ({ icon: Icon, title, value, subtitle, variant = "primary" }) => (
    <Card className="text-center border-0 shadow-sm">
      <Card.Body>
        <div className={`text-${variant} mb-2`}>
          <Icon size={24} />
        </div>
        <h3 className={`text-${variant} mb-1`}>{value}</h3>
        <small className="text-muted">{title}</small>
        {subtitle && <div><small className="text-muted">{subtitle}</small></div>}
      </Card.Body>
    </Card>
  );

  // ===================================================================
  // RENDER
  // ===================================================================

  return (
    <div className="boutiques-container">
      <Container fluid>
        {/* En-tête avec statistiques */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h2 className="mb-1">🏪 Gestion des Boutiques</h2>
                <p className="text-muted mb-0">Administration et configuration de toutes les boutiques du système</p>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-primary" onClick={loadBoutiques}>
                  <RefreshCw size={16} className="me-1" />
                  Actualiser
                </Button>
                <Button variant="success" onClick={handleCreateBoutique}>
                  <Plus size={16} className="me-1" />
                  Nouvelle Boutique
                </Button>
              </div>
            </div>

            {/* Statistiques globales */}
            <Row className="mb-4">
              <Col lg={3} md={6} className="mb-3">
                <StatCard
                  icon={Users}
                  title="Total Boutiques"
                  value={globalStats.totalBoutiques}
                  subtitle={`${globalStats.activeBoutiques} actives`}
                  variant="primary"
                />
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <StatCard
                  icon={TrendingUp}
                  title="Revenus Global"
                  value={formatCurrency(globalStats.totalRevenue)}
                  subtitle="Ce mois"
                  variant="success"
                />
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <StatCard
                  icon={Users}
                  title="Gestionnaires"
                  value={globalStats.totalManagers}
                  subtitle="Total dans le système"
                  variant="info"
                />
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <StatCard
                  icon={Calendar}
                  title="Nouvelles ce mois"
                  value="3"
                  subtitle="+2 vs mois dernier"
                  variant="warning"
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Filtres et recherche */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={4}>
                    <Form.Label>Recherche</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <Search size={16} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Nom, adresse, gestionnaire..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Statut</Form.Label>
                    <Form.Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="ACTIVE">Actives</option>
                      <option value="INACTIVE">Inactives</option>
                      <option value="ARCHIVED">Archivées</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Tri</Form.Label>
                    <Form.Select
                      value={filters.sortBy}
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    >
                      <option value="created_date">Date de création</option>
                      <option value="name">Nom</option>
                      <option value="revenue">Revenus</option>
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <div className="text-muted mt-4">
                      {boutiques.length} boutique(s)
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Liste des boutiques */}
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">📋 Liste des Boutiques</h5>
                  <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm">
                      <Filter size={14} className="me-1" />
                      Filtres avancés
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                ) : boutiques.length > 0 ? (
                  <>
                    {/* Vue cards pour écrans moyens et petits */}
                    <div className="d-lg-none p-3">
                      <Row>
                        {boutiques.map(boutique => (
                          <Col md={6} className="mb-3" key={boutique.id}>
                            <BoutiqueCard 
                              boutique={boutique}
                              onEdit={handleEditBoutique}
                              onConfigure={handleConfigureBoutique}
                              onArchive={handleArchiveBoutique}
                              onView={handleViewDetails}
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>

                    {/* Vue tableau pour grands écrans */}
                    <div className="d-none d-lg-block">
                      <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Boutique</th>
                            <th>Gestionnaire</th>
                            <th>Localisation</th>
                            <th>Statut</th>
                            <th>Revenus (mois)</th>
                            <th>Créée le</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {boutiques.map(boutique => (
                            <tr key={boutique.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  {/* Logo ou initial */}
                                  <div className="me-3">
                                    {boutique.logoUrl ? (
                                      <img
                                        src={boutique.logoUrl}
                                        alt={boutique.name}
                                        className="rounded"
                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <div 
                                        className="rounded d-flex align-items-center justify-content-center"
                                        style={{ 
                                          width: '40px', 
                                          height: '40px', 
                                          backgroundColor: boutique.primary_color || '#3b82f6',
                                          color: 'white',
                                          fontSize: '16px',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        {boutique.name?.charAt(0) || 'B'}
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <strong>{boutique.name}</strong>
                                    {/* Aperçu couleurs */}
                                    <div className="d-flex gap-1 mt-1">
                                      <div
                                        className="rounded-circle border"
                                        style={{
                                          width: '12px',
                                          height: '12px',
                                          backgroundColor: boutique.primary_color || '#3b82f6'
                                        }}
                                        title="Couleur primaire"
                                      />
                                      <div
                                        className="rounded-circle border"
                                        style={{
                                          width: '12px',
                                          height: '12px',
                                          backgroundColor: boutique.secondary_color || '#1d4ed8'
                                        }}
                                        title="Couleur secondaire"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <div>{boutique.manager_name || 'Non assigné'}</div>
                                  <small className="text-muted">{boutique.manager_email}</small>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <div className="d-flex align-items-center mb-1">
                                    <MapPin size={12} className="me-1 text-muted" />
                                    <small>{boutique.address}</small>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <Phone size={12} className="me-1 text-muted" />
                                    <small>{boutique.phone}</small>
                                  </div>
                                </div>
                              </td>
                              <td>{getStatusBadge(boutique.status)}</td>
                              <td>
                                <div>
                                  <strong className="text-success">
                                    {formatCurrency(boutique.monthlyRevenue || 0)}
                                  </strong>
                                  {boutique.revenueGrowth && (
                                    <small className={`d-block ${boutique.revenueGrowth > 0 ? 
                                      'text-success' : 'text-danger'}`}>
                                      {boutique.revenueGrowth > 0 ? '+' : ''}{boutique.revenueGrowth}%
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatDate(boutique.created_at)}
                                </small>
                              </td>
                              <td>
                                <div className="d-flex justify-content-center gap-1">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={() => handleConfigureBoutique(boutique)}
                                    title="Configurer"
                                  >
                                    <Settings size={14} />
                                  </Button>
                                  <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    onClick={() => handleEditBoutique(boutique)}
                                    title="Modifier"
                                  >
                                    <Edit size={14} />
                                  </Button>
                                  <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteBoutique(boutique)}
                                      disabled={deleteLoading}
                                      title="🚨 SUPPRIMER LA BOUTIQUE COMPLÈTEMENT"
                                  >
                                      <Trash2 size={14} />
                                  </Button>
                                  <Button 
                                    variant="outline-info" 
                                    size="sm" 
                                    onClick={() => handleViewDetails(boutique)}
                                    title="Voir détails"
                                  >
                                    <Eye size={14} />
                                  </Button>

                                  {/* ✅ NOUVEAU : Bouton suppression des ventes */}
                        


<Button
  variant="outline-danger"
  size="sm"
  onClick={() => handleDeleteSales(boutique)}
  disabled={deleteLoading}
  title="⚠️ SUPPRIMER TOUT : Ventes + Stocks + Mouvements"
  className="text-danger"
>
  {deleteLoading ? (
    <span className="spinner-border spinner-border-sm" />
  ) : (
    <>
      <Trash2 size={14} className="me-1" />
      
    </>
  )}
</Button>
                                  <Button 
                                    variant="outline-warning" 
                                    size="sm" 
                                    onClick={() => handleArchiveBoutique(boutique.id)}
                                    title="Archiver"
                                  >
                                    <Archive size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center p-5">
                    <div className="mb-3">
                      <TrendingUp size={48} className="text-muted" />
                    </div>
                    <h5 className="text-muted mb-2">Aucune boutique trouvée</h5>
                    <p className="text-muted mb-3">
                      {filters.search || filters.status !== 'all' 
                        ? 'Aucune boutique ne correspond à vos critères de recherche.'
                        : 'Commencez par créer votre première boutique.'
                      }
                    </p>
                    <Button variant="success" onClick={handleCreateBoutique}>
                      <Plus size={16} className="me-1" />
                      Créer une boutique
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ✅ NOUVEAU : Modal de confirmation de suppression des ventes */}
      <DeleteSalesConfirmationModal
        isOpen={showDeleteSalesModal}
        onClose={handleCloseDeleteSalesModal}
        onConfirm={handleConfirmDeleteSales}
        boutique={selectedBoutiqueForDeleteSales}
        confirmationData={salesConfirmationData}
        loading={deleteLoading}
      />

      {/* Modal de création/édition */}
      <Modal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)}
        size="lg"
        backdrop="static"
      >
        <CreateBoutique 
          boutique={selectedBoutique}
          onClose={() => {
            setShowCreateModal(false);
            clearErrors();
          }}
          onSave={handleSaveBoutique}
          errors={errors}
        />
      </Modal>

      {/* ✅ MODAL DE CONFIGURATION - NOUVEAU */}
      <BoutiqueConfigModal
        show={showConfigModal}
        onHide={handleCloseConfig}
        boutique={selectedBoutiqueForConfig}
        onSave={handleSaveConfig}
      />

      {/* Modal détails boutique */}
      <Modal 
        show={showDetailsModal} 
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            👁️ Détails - {selectedBoutique?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBoutique && (
            <div>
              <Row>
                <Col md={6}>
                  <h6>Informations générales</h6>
                  <p><strong>Nom:</strong> {selectedBoutique.name}</p>
                  <p><strong>Description:</strong> {selectedBoutique.description || 'Aucune'}</p>
                  <p><strong>Adresse:</strong> {selectedBoutique.address}</p>
                  <p><strong>Téléphone:</strong> {selectedBoutique.phone}</p>
                  <p><strong>Email:</strong> {selectedBoutique.email || 'Aucun'}</p>
                </Col>
                <Col md={6}>
                  <h6>Configuration</h6>
                  <p><strong>Gestionnaire:</strong> {selectedBoutique.manager_name || 'Non assigné'}</p>
                  <p><strong>Statut:</strong> {getStatusBadge(selectedBoutique.status)}</p>
                  <p><strong>Créée le:</strong> {formatDate(selectedBoutique.created_at)}</p>
                  <div className="mt-3">
                    <strong>Couleurs:</strong>
                    <div className="d-flex gap-2 mt-1">
                      <div
                        className="rounded border"
                        style={{
                          width: '30px',
                          height: '30px',
                          backgroundColor: selectedBoutique.primary_color || '#3b82f6'
                        }}
                        title="Couleur primaire"
                      />
                      <div
                        className="rounded border"
                        style={{
                          width: '30px',
                          height: '30px',
                          backgroundColor: selectedBoutique.secondary_color || '#1d4ed8'
                        }}
                        title="Couleur secondaire"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </Button>
          <Button variant="primary" onClick={() => handleConfigureBoutique(selectedBoutique)}>
            <Settings size={16} className="me-1" />
            Configurer
          </Button>
        </Modal.Footer>
      </Modal>

      {showDeleteBoutiqueModal && (
    <Modal show onHide={() => setShowDeleteBoutiqueModal(false)} size="lg" centered backdrop="static">
        <Modal.Header className="bg-danger text-white" closeButton>
            <Modal.Title>🚨 SUPPRESSION DÉFINITIVE DE LA BOUTIQUE</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Alert variant="danger">
                <h6>Vous allez supprimer DÉFINITIVEMENT : <strong>{selectedBoutiqueForDelete?.name}</strong></h6>
                <p className="mb-0">Cette action supprime la boutique et TOUTES ses données. C'est irréversible.</p>
            </Alert>
            {boutiqueDeleteConfirmData && (
                <div className="mb-3">
                    <h6>📊 Données qui seront supprimées :</h6>
                    <ul>
                        <li><strong>{boutiqueDeleteConfirmData.productsCount}</strong> produits</li>
                        <li><strong>{boutiqueDeleteConfirmData.categoriesCount}</strong> catégories</li>
                        <li><strong>{boutiqueDeleteConfirmData.salesCount}</strong> ventes</li>
                        <li><strong>{boutiqueDeleteConfirmData.paymentsCount}</strong> paiements</li>
                        <li><strong>{boutiqueDeleteConfirmData.customersCount}</strong> clients</li>
                        <li><strong>{boutiqueDeleteConfirmData.movementsCount}</strong> mouvements de stock</li>
                        <li><strong>{boutiqueDeleteConfirmData.expensesCount}</strong> dépenses</li>
                        <li><strong>{boutiqueDeleteConfirmData.usersCount}</strong> utilisateurs</li>
                    </ul>
                </div>
            )}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteBoutiqueModal(false)}>Annuler</Button>
            <Button variant="danger" onClick={handleConfirmDeleteBoutique} disabled={deleteLoading}>
                {deleteLoading ? <><span className="spinner-border spinner-border-sm me-2" />Suppression...</> 
                    : <><Trash2 size={16} className="me-2" />SUPPRIMER DÉFINITIVEMENT</>}
            </Button>
        </Modal.Footer>
    </Modal>
)}

      {/* Styles CSS intégrés */}
      <style jsx>{`
        .boutiques-container {
          padding: 2rem 0;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .stat-card {
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .boutique-card {
          transition: all 0.3s ease;
          border: 1px solid #dee2e6;
        }

        .boutique-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }

        .action-buttons {
          display: flex;
          gap: 0.25rem;
          justify-content: center;
        }

        .action-buttons .btn {
          padding: 0.375rem 0.5rem;
          border-radius: 0.375rem;
        }

        .table th {
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
          background: #f8f9fa !important;
        }

        .table td {
          vertical-align: middle;
          padding: 1rem 0.75rem;
        }

        .table tr:hover {
          background-color: rgba(0, 123, 255, 0.05);
        }

        @media (max-width: 768px) {
          .boutiques-container {
            padding: 1rem 0;
          }
        }
      `}</style>
    </div>
  );
};

export default BoutiquesList;